// SettingsButton.tsx
import React, { useState } from 'react';
import { useSettings } from '../contexts/SettingsContext';

export function SettingsButton() {
  const {
    fontSize,
    increaseFontSize,
    decreaseFontSize,
    isDarkMode,
    isAutoMode,
    setIsAutoMode,
    toggleDarkMode,
    selectedVoice,
    setSelectedVoice,
    voiceOptions,
    speechRate,
    setSpeechRate,
  } = useSettings();
  const [isOpen, setIsOpen] = useState(false);

  const handleVoiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const voice = voiceOptions.find(v => v.name === e.target.value);
    if (voice) setSelectedVoice(voice);
  };

  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSpeechRate(parseFloat(e.target.value));
  };

  const handleThemeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === 'auto') {
      setIsAutoMode(true);
      // The useEffect in SettingsContext will handle the time-based theme update
    } else {
      setIsAutoMode(false);
      if (value === 'dark' && !isDarkMode) {
        toggleDarkMode();
      } else if (value === 'light' && isDarkMode) {
        toggleDarkMode();
      }
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
      >
        Settings
      </button>

      {isOpen && (
        <div className="absolute mt-2 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg w-64 z-10">
          {/* Font Size */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Font Size: {fontSize}px
            </label>
            <div className="flex space-x-2">
              <button
                onClick={decreaseFontSize}
                className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                -
              </button>
              <button
                onClick={increaseFontSize}
                className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                +
              </button>
            </div>
          </div>

          {/* Theme Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Theme
            </label>
            <select
              value={isAutoMode ? 'auto' : isDarkMode ? 'dark' : 'light'}
              onChange={handleThemeChange}
              className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <option value="auto">Auto</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </div>

          {/* Voice Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Voice
            </label>
            <select
              value={selectedVoice?.name || ''}
              onChange={handleVoiceChange}
              className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              {voiceOptions.map(voice => (
                <option key={voice.name} value={voice.name}>
                  {voice.label}
                </option>
              ))}
            </select>
          </div>

          {/* Speech Rate */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Speech Rate: {speechRate.toFixed(1)}
            </label>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={speechRate}
              onChange={handleRateChange}
              className="w-full accent-blue-500"
            />
          </div>
        </div>
      )}
    </div>
  );
}